import React, { useCallback, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  Box,
  Button,
  HtmlTrans,
  stringToSemanticDisplay,
  TextFieldForm,
  Typography,
} from 'web-library';
import { useGetIExamMasterCode } from '../../../../api-refactored/user/useGetIExamMasterCode';
import { useGetOrganisationList } from '../../../../api-refactored/user/useGetOrganisationList';
import { IExamMasterCodeItem, LookupListApiItem, LookupListItem } from '../../../../api/user/model';
import ControlledAutocomplete from '../../../../components/form-items/ControlledAutocomplete';
import ControlledCustomSelect from '../../../../components/form-items/ControlledCustomSelect';
import {
  CandidateProfileFormData,
  CandidateProfileFormDefaultValues,
  CandidateProfileIsDisableInputs,
  IdTypeType,
} from '../../../../models';
import {
  DROPDOWN_PLACEHOLDER,
  getIExamDropdownItemLabel,
  getIExamMasterCodeSelected,
} from '../../logic/useCandidateProfileForm';
import CandidateNameRelatedComponent from './HelperComponents/CandidateNameRelatedComponent';
import CandidateNationalityRelatedComponent from './HelperComponents/CandidateNationalityRelatedComponent';
import ToggleDisplayEditFormItem from '../../../../components/ToggleDisplayEditFormItem';
import { useStyles } from './styles';
import { usePrCitizenshipForm } from '../../../accountManagement/logic/usePrCitizenshipForm';
import ControlledDatePicker from 'web-library/stories/atom/DatePicker/ControlledDatePicker';

export const ID_TYPE_MENU_ITEMS = [
  { key: 'I', value: 'I', children: 'NRIC/FIN' },
  { key: 'P', value: 'P', children: 'Passport No.' },
];
const getIDTypeDisplayString = (IdType?: IdTypeType) =>
  ID_TYPE_MENU_ITEMS.find(({ value }) => value === IdType)?.children;

export interface CandidateProfilePersonalParticularsProps {
  isEditState: boolean;
  formHook: UseFormReturn<CandidateProfileFormData>;
  isDisableInputs?: CandidateProfileIsDisableInputs;
  isHideExtraFormInfo?: boolean;
  formDefaultValues?: CandidateProfileFormDefaultValues;
  formDefaultValuesViewDetails?: CandidateProfileFormDefaultValues;
  isRequestProfileAmendmentOpen?: boolean;
  handleClickRequestForAmendment?: () => void;
}

export interface ToggleDisplayEditBirthDateProps {
  isEditState: boolean;
  formHook: UseFormReturn<CandidateProfileFormData>;
  isDisableInputs?: CandidateProfileIsDisableInputs;
  formDefaultValues?: CandidateProfileFormDefaultValues;
  formDefaultValuesViewDetails?: CandidateProfileFormDefaultValues;
}

const ToggleDisplayEditBirthDate = ({
  isDisableInputs,
  isEditState,
  formDefaultValues,
  formDefaultValuesViewDetails,
  formHook,
}: ToggleDisplayEditBirthDateProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const {
    control,
    formState: { errors },
    setValue,
  } = formHook;
  const [dateOfBirth, setDateOfBirth] = useState<string | undefined>(
    formDefaultValues?.dateOfBirth
  );
  const dateOfBirthTranslationValue = t('profile->>CreateCandidateForm.Date of Birth');

  const handleDateOfBirthOnChange = useCallback(
    (date: string | undefined) => {
      setDateOfBirth(date || '');
      setValue('dateOfBirth', date || '');
    },
    [setValue]
  );

  return (
    <>
      <ToggleDisplayEditFormItem
        isEditState={isEditState}
        labelText={dateOfBirthTranslationValue}
        valueText={stringToSemanticDisplay(formDefaultValuesViewDetails?.dateOfBirth as string)}
        containerClassName={classes.formElementContainer}
        EditComponent={
          formHook && (
            <ControlledDatePicker
              label={dateOfBirthTranslationValue}
              value={dateOfBirth}
              onChange={handleDateOfBirthOnChange}
              inputVariant="filled"
              name="dateOfBirth"
              control={control}
              error={!!errors.dateOfBirth}
              helperText={errors?.dateOfBirth?.message?.toString()}
              readOnly={isDisableInputs?.dateOfBirth}
              disableFuture
              disabled={isDisableInputs?.dateOfBirth && isEditState}
              defaultValue={dateOfBirth}
              isRequired
            />
          )
        }
      />
    </>
  );
};

const CandidateProfilePersonalParticulars = ({
  isEditState,
  formHook,
  isDisableInputs = undefined,
  isHideExtraFormInfo = false,
  formDefaultValues = undefined,
  formDefaultValuesViewDetails = undefined,
  isRequestProfileAmendmentOpen,
  handleClickRequestForAmendment,
}: CandidateProfilePersonalParticularsProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
  } = formHook;

  const {
    data: dataIEXAM,
    isLoading: isLoadingIEXAM,
    error: errorMasterCode,
    refetch: refetchMasterCode,
  } = useGetIExamMasterCode();

  const {
    data: dataOrganisation,
    isLoading: isLoadingOrganisation,
    error: errorOrganisation,
    refetch: refetchOrganisation,
  } = useGetOrganisationList({ type: 'NMOE' });

  const prCitizenHook = usePrCitizenshipForm({
    formHook,
    isDisableInputs,
    formDefaultValues,
  });

  return (
    <Accordion defaultExpanded header={t('profile->>CreateCandidateForm.PersonalParticulars')}>
      {isRequestProfileAmendmentOpen && (
        <Box className={classes.requestForAmendmentButtonContainer}>
          <Typography className={classes.requestForAmendmentText}>
            {t('profile->>CreateCandidateForm.PersonalParticularsStatement')}
          </Typography>
          <Button
            className={classes.requestForAmendmentButton}
            variant="outlined"
            onClick={handleClickRequestForAmendment}
          >
            {t('profile->>CreateCandidateForm.RequestForAmendment')}
          </Button>
        </Box>
      )}
      <Box className={classes.responsiveFlexContainer}>
        <CandidateNameRelatedComponent
          isEditState={isEditState}
          formHook={formHook}
          isDisableInputs={isDisableInputs}
          isHideExtraFormInfo={isHideExtraFormInfo}
          formDefaultValues={formDefaultValues}
          formDefaultValuesViewDetails={formDefaultValuesViewDetails}
        />
        <ToggleDisplayEditFormItem
          isEditState={isEditState}
          labelText={t('profile->>CreateCandidateForm.ID Type')}
          valueText={getIDTypeDisplayString(formDefaultValuesViewDetails?.IdType)}
          containerClassName={`${classes.formElementContainer} ${classes.halfWidthLeftItem}`}
          /* CPEPOKPNEH-2662 - To include IdHelpText */
          EditComponent={
            <ControlledCustomSelect
              labelId="inputSelectLabel-idType"
              id="inputSelect-idType"
              label={
                <Box>
                  <Box>{t('profile->>CreateCandidateForm.IDType')}</Box>
                  <Box className={classes.helperText}>
                    <HtmlTrans
                      i18nKey="profile->>CreateCandidateForm.IdHelpText"
                      values={{
                        boldContent: 'only if',
                      }}
                    />
                    <br />
                    {t('profile->>CreateCandidateForm.IdHelpText2')}
                  </Box>
                </Box>
              }
              name="IdType"
              control={control}
              menuItems={ID_TYPE_MENU_ITEMS}
              defaultValue="I"
              variant="filled"
              disabled={isDisableInputs?.IdType}
              fullWidth
              onChange={prCitizenHook.onIdTypeChange}
            />
          }
        />
        <ToggleDisplayEditFormItem
          isEditState={isEditState}
          labelText={t('profile->>CreateCandidateForm.NRIC/FIN/Passport No.')}
          valueText={formDefaultValuesViewDetails?.NricFin}
          containerClassName={`${classes.formElementContainer} ${classes.halfWidthRightItem}`}
          EditComponent={
            <TextFieldForm
              form={formHook}
              className={classes.nricFin}
              label={t('profile->>CreateCandidateForm.NRIC/FIN/Passport No.')}
              name="NricFin"
              type="text"
              error={!!errors.NricFin}
              helperText={errors?.NricFin?.message?.toString()}
              fullWidth
              inputProps={{ readOnly: isDisableInputs?.NricFin }}
              onChange={prCitizenHook.onIdChange}
              isRequired
            />
          }
        />
        <ToggleDisplayEditBirthDate
          isDisableInputs={isDisableInputs}
          isEditState={isEditState}
          formDefaultValues={formDefaultValues}
          formDefaultValuesViewDetails={formDefaultValuesViewDetails}
          formHook={formHook}
        />
        <ToggleDisplayEditFormItem
          isEditState={isEditState}
          labelText={t('profile->>CreateCandidateForm.Gender')}
          valueText={formDefaultValuesViewDetails?.gender?.codeValueDescription}
          containerClassName={classes.formElementContainer}
          EditComponent={
            <ControlledAutocomplete<IExamMasterCodeItem, false, false, undefined>
              label={t('profile->>CreateCandidateForm.Gender')}
              control={control}
              name="gender"
              id="input-select-gender"
              error={!!errors?.gender}
              helperText={errors?.gender?.message?.toString()}
              loading={isLoadingIEXAM}
              loadingError={!!errorMasterCode}
              loadingErrorOnReFetch={refetchMasterCode}
              options={dataIEXAM ? dataIEXAM.gender : []}
              getOptionLabel={getIExamDropdownItemLabel}
              placeholder={DROPDOWN_PLACEHOLDER}
              disabled={isDisableInputs?.gender}
              isOptionEqualToValue={getIExamMasterCodeSelected}
              isRequired
            />
          }
        />
        <ToggleDisplayEditFormItem
          isEditState={isEditState}
          labelText={t('profile->>CreateCandidateForm.Race')}
          valueText={formDefaultValuesViewDetails?.race?.codeValueDescription}
          containerClassName={classes.formElementContainer}
          EditComponent={
            <ControlledAutocomplete<IExamMasterCodeItem, false, false, undefined>
              label={t('profile->>CreateCandidateForm.Race')}
              control={control}
              name="race"
              id="input-select-race"
              error={!!errors.race}
              helperText={errors?.race?.message?.toString()}
              loading={isLoadingIEXAM}
              loadingError={!!errorMasterCode}
              loadingErrorOnReFetch={refetchMasterCode}
              getOptionLabel={getIExamDropdownItemLabel}
              options={dataIEXAM ? dataIEXAM.race : []}
              placeholder={DROPDOWN_PLACEHOLDER}
              disabled={isDisableInputs?.race}
              isOptionEqualToValue={getIExamMasterCodeSelected}
              isRequired
            />
          }
        />
        <CandidateNationalityRelatedComponent
          isEditState={isEditState}
          formHook={formHook}
          isDisableInputs={isDisableInputs}
          isHideExtraFormInfo={isHideExtraFormInfo}
          formDefaultValues={formDefaultValues}
          prCitizenHook={prCitizenHook}
        />
        <ToggleDisplayEditFormItem
          isEditState={isEditState}
          labelText={t('profile->>CreateCandidateForm.Occupation')}
          allHide
          valueText={formDefaultValuesViewDetails?.occupation?.description}
          containerClassName={classes.formElementContainer}
          EditComponent={
            <ControlledAutocomplete<LookupListApiItem, false, false, undefined>
              label={t('profile->>CreateCandidateForm.Occupation')}
              control={control}
              name="occupation"
              id="input-select-occupation"
              error={!!errors.occupation}
              helperText={errors?.occupation?.message?.toString()}
              loading={isLoadingIEXAM}
              loadingError={!!errorMasterCode}
              loadingErrorOnReFetch={refetchMasterCode}
              options={dataIEXAM ? dataIEXAM.occupation : []}
              getOptionLabel={(name: LookupListApiItem) => name.description}
              placeholder={DROPDOWN_PLACEHOLDER}
              disabled={isDisableInputs?.occupation}
              isOptionEqualToValue={(option, value) => `${option.id}` === `${value.id}`}
            />
          }
        />
        <ToggleDisplayEditFormItem
          isEditState={isEditState}
          labelText={t('profile->>CreateCandidateForm.School')}
          valueText={formDefaultValuesViewDetails?.school?.name}
          containerClassName={classes.formElementContainer}
          EditComponent={
            <ControlledAutocomplete<LookupListItem, false, false, undefined>
              label={t('profile->>CreateCandidateForm.School')}
              control={control}
              name="school"
              id="input-select-school"
              error={!!errors.school}
              helperText={errors?.school?.message?.toString()}
              loading={isLoadingOrganisation}
              loadingError={!!errorOrganisation}
              loadingErrorOnReFetch={refetchOrganisation}
              options={dataOrganisation || []}
              getOptionLabel={({ name }) => name}
              placeholder={DROPDOWN_PLACEHOLDER}
              disabled={isDisableInputs?.school}
              isOptionEqualToValue={(option, value) => `${option.id}` === `${value.id}`}
            />
          }
        />
      </Box>
    </Accordion>
  );
};
export default CandidateProfilePersonalParticulars;
